import { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useTeamStore } from '@/stores/team'
import { signOut } from '@/lib/firebase'
import { GET_TEAM_ADMINS_BY_PROFILE_ID, GET_PROFILE_BY_ID } from '@/graphql/queries/auth'
import { GET_TEAM, GET_TEAM_CREDITS, GET_ALL_TEAMS } from '@/graphql/queries/team'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeProfile(raw: any): import('@/types').Profile {
  const emails = raw.emailAddresses ?? []
  const primaryEmail = emails.find((e: { primary: boolean }) => e.primary) ?? emails[0]
  return {
    id: raw.id,
    firstName: raw.firstName ?? null,
    lastName: raw.lastName ?? null,
    email: primaryEmail?.emailAddress ?? null,
    phone: raw.phoneMobile ?? null,
    avatarURL: raw.photo ?? null,
    global_admin: !!raw.globalAdmin,
    createdAt: raw.createdAt,
  }
}

export function useBootstrap() {
  const navigate = useNavigate()
  const {
    firebaseUser,
    selectedTeamId,
    isGlobalAdmin,
    redeemingInvite,
    setTeamAdmins,
    setProfile,
    setSelectedTeamId,
    setNotAdminError,
    reset: resetAuth,
  } = useAuthStore()
  const { setTeam, setAllTeams, setBranding, setCredits, reset: resetTeam } = useTeamStore()

  const profileId = firebaseUser?.uid

  const { data: adminData, loading: adminLoading } = useQuery(GET_TEAM_ADMINS_BY_PROFILE_ID, {
    variables: { profileId: profileId! },
    skip: !profileId,
  })

  // Always fetch profile directly — needed for globalAdmin flag regardless of team_admin records
  const { data: profileData, loading: profileLoading } = useQuery(GET_PROFILE_BY_ID, {
    variables: { id: profileId! },
    skip: !profileId,
  })

  // Determine globalAdmin from profile data (don't wait for store)
  const profileIsGlobalAdmin = !!profileData?.profile?.globalAdmin

  // Fetch all teams for globaladmins
  const { data: allTeamsData } = useQuery(GET_ALL_TEAMS, {
    variables: { limit: 1000 },
    skip: !profileIsGlobalAdmin,
  })

  const { data: teamData, loading: teamLoading } = useQuery(GET_TEAM, {
    variables: { id: selectedTeamId! },
    skip: !selectedTeamId,
  })

  const { data: creditsData } = useQuery(GET_TEAM_CREDITS, {
    variables: { teamId: selectedTeamId! },
    skip: !selectedTeamId,
  })

  // Compute aggregate credits from credit rows
  const computeCredits = (rows: Array<{ creditType: string; quantity: number; teamCreditUsage: number }>) => {
    let video = 0,
      instant = 0,
      pro = 0
    for (const row of rows) {
      const available = (row.quantity ?? 0) - (row.teamCreditUsage ?? 0)
      if (row.creditType === 'video') video += available
      else if (row.creditType === 'instant') instant += available
      else if (row.creditType === 'pro') pro += available
    }
    return { video, instant, pro }
  }

  // Set profile from direct query
  useEffect(() => {
    if (!profileData?.profile) return
    const profile = normalizeProfile(profileData.profile)
    setProfile(profile)
  }, [profileData, setProfile])

  // Process team admins response — set team admin records and team list for non-globaladmins
  useEffect(() => {
    if (!adminData?.getTeamAdminsByProfileId?.rows) return

    const admins = adminData.getTeamAdminsByProfileId.rows
    setTeamAdmins(admins)

    // For non-globaladmins, set teams from admin records
    if (!profileIsGlobalAdmin && admins.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const teams = admins.map((a: { team: any }) => ({
        id: a.team.id,
        name: a.team.name,
        logoURL: a.team.teamLogoURL ?? null,
        tags: a.team.tags ?? [],
        slug: a.team.slug ?? null,
        customPushNotificationAccess: !!a.team.customPushNotificationAccess,
        maxActiveTemplates: a.team.maxActiveTemplates ?? null,
        maxTeamStorageMB: a.team.maxTeamStorageMB ?? null,
        parentTeamId: a.team.teamParentId ?? null,
      }))
      setAllTeams(teams)
    }
  }, [adminData, profileIsGlobalAdmin, setTeamAdmins, setAllTeams])

  // Kick out anyone who isn't an admin of any team and isn't a global admin — team-admin
  // records are the only real avenue to access in this app, so zero rows means nothing to
  // manage here. Waits for both queries to resolve so a slow profile fetch can't be
  // misread as "definitely not a global admin" and wrongly log one out. Also skipped entirely
  // while an invite redemption is in flight (LoginPage) — a brand-new account genuinely has
  // zero team_admin rows until redeemTeamAdminInvite creates one moments later, and this
  // effect's own adminData fetch has no way to know that's about to happen.
  useEffect(() => {
    if (redeemingInvite) return
    if (!adminData?.getTeamAdminsByProfileId || !profileData?.profile) return

    const admins = adminData.getTeamAdminsByProfileId.rows
    if (admins.length === 0 && !profileIsGlobalAdmin) {
      setNotAdminError(true)
      resetAuth()
      resetTeam()
      signOut()
      navigate('/login')
    }
  }, [adminData, profileData, profileIsGlobalAdmin, redeemingInvite, setNotAdminError, resetAuth, resetTeam, navigate])

  // For globaladmins, populate allTeams from the full teams list
  useEffect(() => {
    if (!isGlobalAdmin || !allTeamsData?.getTeams?.rows) return

    const teams = allTeamsData.getTeams.rows.map(
      (t: {
        id: string
        name: string
        teamLogoURL: string | null
        tags: string[]
        slug: string | null
        customPushNotificationAccess: boolean
        teamParentId: string | null
      }) => ({
        id: t.id,
        name: t.name,
        logoURL: t.teamLogoURL,
        tags: t.tags ?? [],
        slug: t.slug,
        customPushNotificationAccess: t.customPushNotificationAccess,
        maxActiveTemplates: null,
        maxTeamStorageMB: null,
        parentTeamId: t.teamParentId,
      }),
    )
    setAllTeams(teams)

    // Auto-select first team if none selected
    if (!selectedTeamId && teams.length > 0) {
      setSelectedTeamId(teams[0].id)
    }
  }, [isGlobalAdmin, allTeamsData, setAllTeams, selectedTeamId, setSelectedTeamId])

  // Process team data
  useEffect(() => {
    if (teamData?.getTeam) {
      const t = teamData.getTeam
      setTeam({
        id: t.id,
        name: t.name,
        logoURL: t.teamLogoURL ?? null,
        tags: t.tags ?? [],
        slug: t.slug ?? null,
        customPushNotificationAccess: !!t.customPushNotificationAccess,
        maxActiveTemplates: t.maxActiveTemplates ?? null,
        maxTeamStorageMB: t.maxTeamStorageMB ?? null,
        parentTeamId: t.teamParentId ?? null,
        planName: t.planName ?? null,
        planExpiresAt: t.planExpiresAt ?? null,
      })
    } else if (teamData && teamData.getTeam === null && selectedTeamId) {
      // selectedTeamId is persisted in localStorage and survives switching which API host the
      // frontend points at locally (dev/staging/prod each have their own database) — a team ID
      // valid on one environment is usually meaningless on another. Without this, `team` in the
      // store just stays whatever it was before (often undefined), teamLoading still flips to
      // false since the query did resolve, and Layout's noPlan check reads that as "confirmed no
      // plan" with zero real team context, flashing the trial modal/banner. Clearing the stale id
      // lets the auto-select effect above pick a real team for this environment instead.
      setSelectedTeamId(null)
    }
  }, [teamData, setTeam, selectedTeamId, setSelectedTeamId])

  // Process branding data
  useEffect(() => {
    if (teamData?.getTeam?.teamBranding) {
      setBranding(teamData.getTeam.teamBranding)
    }
  }, [teamData, setBranding])

  // Process credits data
  useEffect(() => {
    if (creditsData?.getTeamCredits?.rows) {
      setCredits(computeCredits(creditsData.getTeamCredits.rows))
    }
  }, [creditsData, setCredits])

  // True until the team is fully resolved — covers the whole chain, not just GET_TEAM.
  // !selectedTeamId is its own unconditional condition (not &&-gated with teamLoading): if we
  // don't even know the team ID yet, we're definitely not done, regardless of what adminLoading/
  // teamLoading happen to report in the same render (e.g. a cache hit on adminData can report
  // loading: false in the same tick its own setSelectedTeamId effect hasn't run yet — an
  // ordering race, not a real "we're done" signal). Also true during an in-flight invite
  // redemption (LoginPage) — team_admins/selectedTeamId are mid-flux for the same reason the
  // "not an admin" kickout below is suppressed. Lets callers (e.g. Layout's no-plan gating) avoid
  // reading team.planName before it's actually been fetched, where it would still be null from
  // useTeamStore's initial state and look identical to a real no-plan team. profileLoading is
  // included for the same reason: Layout's no-plan gating also reads isGlobalAdmin (derived from
  // profileData, set via setProfile) to decide StartTrialModal vs. NoTrialBanner — without this,
  // team.planName could resolve before profileData does, briefly reading a real global admin as
  // not-an-admin and flashing StartTrialModal.
  const teamStillLoading = redeemingInvite || adminLoading || profileLoading || !selectedTeamId || teamLoading
  return { teamLoading: teamStillLoading }
}
